import { call } from '../../lib/api.js'
import { parseRoute, routeHref } from '../../lib/router.js'
import { DEV } from '../../lib/env.js'
import { connectLive, disconnectLive } from '../../lib/live.js'
import { forgetPush, syncPush } from '../../lib/push.js'
import { browserZone, guessZone, setUserZone } from '../../lib/time.js'
import { loadPage } from '../../lib/pages.js'
import { mailboxName } from '../../lib/mail.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- app-shell ---
let __fez_component_0;
{
// The admin app (`/admin`) renders `tpl-admin-<route>`; its home is `tpl-admin`.
const ADMIN_PAGES = { '': 'tpl-admin' }

// The server sends the admin bundle only with `/admin`. Read on every use: a key sign-in there moves
// to the mail app with replaceState.
const inAdminApp = () => window.location.pathname.replace(/\/+$/, '') === '/admin'

// Routes the Mail rail item stands for.
const MAIL_ROUTES = ['inbox', 'compose']
const ACCOUNT_ROUTES = ['settings', 'sessions', 'feat']

const LOGIN_ERRORS = {
  expired: 'That sign-in link expired. Please try again.',
  unavailable: 'The sign-in service is not reachable right now. Please try again in a moment.',
  disabled: 'This account is disabled. Contact the administrator.',
  no_access: 'This email has no access here. Ask an operator to add you.',
}
__fez_component_0 = class {
  GLOBAL = 'App'

  init() {
    this.state.phase = window.location.pathname.replace(/\/+$/, '') === '/api' ? 'explorer' : 'loading'
    this.state.route = this.parse(window.location.hash)
    this.state.loginError = LOGIN_ERRORS[new URLSearchParams(window.location.search).get('login_error')] || ''
    if (this.state.loginError) window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash}`)
    this.state.dev = DEV
    this.state.renaming = false
    this.state.savingName = false
    this.state.profileErrors = {}
    this.on(window, 'hashchange', () => {
      this.state.route = this.parse(window.location.hash)
      this.land()
    })
    this.subscribe('live:access', () => this.refreshMe())
    if (this.state.phase === 'loading') this.boot()
  }

  parse(hash) {
    return parseRoute(hash, inAdminApp() ? '' : 'inbox')
  }

  async boot() {
    try {
      await this.refreshMe()
      this.land()
      this.state.phase = this.welcoming() ? 'welcome' : 'ready'
      connectLive()
      if (this.globalState.me && !this.globalState.sudo) syncPush()
    } catch (error) {
      if (error.code !== 'unauthorized') this.state.bootError = error.message
      this.state.phase = 'signin'
    }
  }

  /** Reloads who is signed in: a person with their orgs and mailboxes, or one mailbox. */
  async refreshMe() {
    const me = await this.api('auth/me', {}, { silent: true })
    if (me.user && !me.user.time_zone && !me.sudo) me.user = await this.saveZone()
    this.globalState.sudo = Boolean(me.sudo)
    this.globalState.me = me.user
    setUserZone(me.user?.time_zone)
    this.globalState.mailboxSession = me.mailbox
    this.globalState.isAdmin = me.is_admin
    if (me.mailbox) {
      this.globalState.orgs = []
      this.globalState.mailboxes = [me.mailbox]
      return me
    }
    const [orgs, mailboxes] = await Promise.all([
      this.api('org/list', {}, { silent: true }),
      this.api('mailbox/list', {}, { silent: true }),
    ])
    this.globalState.orgs = orgs.items
    this.globalState.mailboxes = mailboxes.items
    return me
  }

  /**
   * A person without a time zone (first sign-in): saves the browser's; when it names none (or
   * the server does not know it), asks with a guess from this device's clock until one is chosen.
   */
  async saveZone() {
    let zone = browserZone()
    for (;;) {
      if (zone) {
        try {
          return await this.api('user/update', { time_zone: zone }, { silent: true })
        } catch (error) {
          if (error.code !== 'validation_failed') throw error
        }
      }
      zone = await window.Dialog.open('time-zone-dialog', { value: zone || guessZone() }, { title: 'Your time zone', size: 'sm' })
    }
  }

  // Operators and the system admin reach the admin pages; everyone else only reads mail.
  isOperator() {
    return Boolean(this.globalState.isAdmin) || (this.globalState.orgs || []).length > 0
  }

  async stopSudo() {
    const target = this.globalState.me
    await this.api('auth/sudo_stop', {}, { silent: true })
    window.location.assign(`/admin${routeHref('user', { user: target.ref })}`)
  }

  // A person who has not finished the welcome steps: no username yet. Sudo never shows them.
  welcoming() {
    const me = this.globalState.me
    return Boolean(me && !me.username && !this.globalState.sudo)
  }

  /** The welcome steps are done: `me` is the saved profile. */
  welcomed(me) {
    this.globalState.me = me
    this.state.phase = 'ready'
  }

  // A mailbox session stays on its own mail, compose and calendar pages and the help.
  land() {
    const own = this.globalState.mailboxSession
    if (!own) return
    const { name, query } = this.state.route
    if (['help', 'calendar', 'calendar-label'].includes(name)) return
    if (['inbox', 'compose'].includes(name) && query.container === own.container && (query.mailbox || own.ref) === own.ref) return
    const hash = routeHref('inbox', { container: own.container, mailbox: own.ref })
    window.history.replaceState(null, '', `/${hash}`)
    this.state.route = parseRoute(hash)
  }

  /** POST /api/<path>; returns data (the whole envelope with `envelope: true`, for `meta.next_cursor`), throws ApiError. `keepalive` lets it finish after the page goes away. */
  async api(path, params = {}, { silent = false, envelope: whole = false, keepalive = false } = {}) {
    try {
      const envelope = await call(path, params, { keepalive })
      if (envelope.message && !silent && this.state.phase === 'ready') window.Toast?.success(envelope.message)
      return whole ? envelope : envelope.data
    } catch (error) {
      if (error.code === 'unauthorized' && this.state.phase === 'ready') {
        this.signedOut()
      } else if (!silent && error.code !== 'validation_failed') {
        window.Toast?.error(error.message)
      }
      throw error
    }
  }

  signIn() {
    const back = `${window.location.pathname}${window.location.hash}`
    window.location.href = `/login?redirect_to=${encodeURIComponent(back)}`
  }

  async signOut() {
    try {
      await this.api('auth/logout', {}, { silent: true })
    } finally {
      this.signedOut()
      if (inAdminApp()) window.location.assign('/')
      else window.location.hash = ''
    }
  }

  signedOut() {
    disconnectLive()
    forgetPush()
    this.globalState.me = null
    this.globalState.sudo = false
    setUserZone('')
    this.globalState.mailboxSession = null
    this.state.phase = 'signin'
  }

  startRename() {
    this.state.profileErrors = {}
    this.state.renaming = true
  }

  cancelRename() {
    this.state.renaming = false
  }

  // closing the account menu drops an unsaved rename
  menuToggled(event) {
    if (event.newState === 'closed') this.state.renaming = false
  }

  async saveName(event) {
    event.preventDefault()
    this.state.savingName = true
    try {
      const form = this.formData(event.target)
      this.globalState.me = await this.api('user/update', { name: form.name || '', username: form.username || '' })
      setUserZone(this.globalState.me.time_zone)
      this.state.renaming = false
    } catch (error) {
      this.state.profileErrors = error.details || {}
    } finally {
      this.state.savingName = false
    }
  }

  closeMe() {
    document.getElementById('me-menu')?.hidePopover()
  }

  go(hash) {
    window.location.hash = hash
  }

  toggleTheme() {
    const dark = document.documentElement.classList.toggle('dark')
    window.Theme?.set(dark ? 'dark' : 'light')
    this.state.isDark = dark
  }

  beforeRender() {
    const own = this.globalState.mailboxSession
    this.state.person = Boolean(this.globalState.me)
    // A mailbox session shows the mailbox where a person shows their profile.
    this.state.me = this.globalState.me || (own && {
      name: own.display_name || mailboxName(own),
      email: own.domains.length ? `${mailboxName(own)}@${own.domains[0]}` : mailboxName(own),
    })
    this.state.isDark = document.documentElement.classList.contains('dark')
    const { name, query } = this.state.route
    const admin = inAdminApp()
    this.state.inAdmin = admin
    // the mail app's highlights; in the admin app only Admin is open
    this.state.view = admin ? '' : name
    this.state.inMail = !admin && MAIL_ROUTES.includes(name)
    this.state.inCalendar = !admin && (name === 'calendar' || name === 'calendar-label')
    this.state.inAccount = !admin && ACCOUNT_ROUTES.includes(name)
    // the route name picks the page (`org` -> tpl-admin-org in the admin app); its refs and query are the page props
    this.state.page = admin ? ADMIN_PAGES[name] || `tpl-admin-${name}` : `tpl-${name}`
    // a lazy page (lib/pages.js) mounts once its chunk has loaded; the outlet stays empty until then
    const loading = loadPage(this.state.page)
    if (loading && loading !== this.pageLoad) {
      this.pageLoad = loading
      loading
        .then(() => this.refresh(), (error) => window.Toast?.error(error.message))
        .finally(() => {
          if (this.pageLoad === loading) this.pageLoad = null
        })
    }
    this.state.pageLoading = Boolean(loading)
    this.state.query = query
    this.state.sudo = Boolean(this.globalState.sudo)
    this.state.operator = !this.state.sudo && this.isOperator()
    this.state.systemAdmin = Boolean(this.globalState.isAdmin)
  }

  CSS = `:fez {
display:block;height:100%;.splash{display:grid;place-items:center;height:100vh}.layout{display:grid;grid-template-columns:var(--rail-width) 1fr;height:100vh;height:100dvh}.layout.sudo{grid-template-rows:auto 1fr}.sudo-bar{grid-column:1 / -1;display:flex;align-items:center;justify-content:center;gap:var(--space-3);padding:var(--space-2) var(--space-4);background:var(--color-danger);color:var(--color-text-inverse);font-size:var(--text-sm)}.rail{display:flex;flex-direction:column;align-items:center;gap:var(--space-2);padding:var(--space-3) 0;background:var(--color-rail-bg);color:var(--color-sidebar-text);overflow-y:auto;scrollbar-width:none}.me-link{text-decoration:none;&:hover{text-decoration:none}}.spacer{flex:1}.rail-link{position:relative;display:grid;place-items:center;width:40px;height:40px;flex-shrink:0;border:0;border-radius:var(--radius-lg);background:transparent;color:var(--color-sidebar-text);cursor:pointer;transition:background var(--duration-fast) var(--ease),color var(--duration-fast) var(--ease);&:hover{background:var(--color-sidebar-hover-bg);color:var(--color-sidebar-text-strong);text-decoration:none}&.active{background:var(--color-sidebar-active-bg);color:var(--color-sidebar-active-text)}&.active::before{content:'';position:absolute;top:10px;bottom:10px;left:calc((40px - var(--rail-width)) / 2);width:3px;border-radius:0 var(--radius-sm) var(--radius-sm) 0;background:var(--color-sidebar-active-bg)}}.me{display:grid;place-items:center;width:40px;height:40px;flex-shrink:0;border:0;border-radius:var(--radius-md);background:transparent;cursor:pointer;line-height:0;&:hover{outline:2px solid var(--color-sidebar-hover-bg);outline-offset:2px}&.active{outline:2px solid var(--color-sidebar-active-bg);outline-offset:2px}}.me-menu{position:fixed;inset:auto auto var(--space-3) calc(var(--rail-width) + var(--space-2));width:260px;margin:0;padding:var(--space-2);&:not(:popover-open){display:none}}.me-head{display:flex;align-items:center;gap:var(--space-3);padding:var(--space-2) var(--space-2) var(--space-3);margin-bottom:var(--space-2);border-bottom:1px solid var(--color-border)}.me-who{display:flex;flex-direction:column;flex:1;min-width:0;line-height:var(--leading-tight)}.me-name,.me-email{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.me-name{font-weight:var(--weight-semibold)}.me-email{color:var(--color-text-muted);font-size:var(--text-sm)}.me-rename{display:grid;gap:var(--space-3);padding:var(--space-2) var(--space-2) var(--space-3);margin-bottom:var(--space-2);border-bottom:1px solid var(--color-border)}.me-actions{display:flex;justify-content:flex-end;gap:var(--space-2)}.me-item{display:flex;align-items:center;gap:var(--space-2);width:100%;padding:var(--space-2);border:0;border-radius:var(--radius-md);background:transparent;color:var(--color-text);font:inherit;text-align:left;cursor:pointer;&:hover{background:var(--color-bg-hover)}}main{min-width:0;min-height:0;overflow-y:auto;background:var(--color-bg);> .fez-component-fez{height:100%}}.not-found{max-width:var(--content-width);margin:0 auto;padding:var(--space-10) var(--space-6) var(--space-12)} :mobile {.layout{grid-template-columns:1fr;grid-template-rows:1fr auto}.layout.sudo{grid-template-rows:auto 1fr auto}.rail{order:2;flex-direction:row;padding:var(--space-2) var(--space-3) calc(var(--space-2) + env(safe-area-inset-bottom));overflow-x:auto}.spacer{flex:1}.rail-link.active::before{display:none}.me-menu{inset:auto var(--space-3) calc(40px + 3 * var(--space-2) + env(safe-area-inset-bottom)) auto}}
}`

  HTML = `{#if state.phase === 'explorer'}
<api-explorer></api-explorer>
{:else if state.phase === 'loading'}
<div class="splash"><ui-spinner size="lg"></ui-spinner></div>
{:else if state.phase === 'signin'}
<app-signin error={state.loginError || state.bootError || ''}></app-signin>
{:else if state.phase === 'welcome'}
<app-welcome></app-welcome>
{:else}
<div class="layout" class:sudo={state.sudo}>
{#if state.sudo}
<div class="sudo-bar" role="status">
<ui-icon name="user-switch" size="18"></ui-icon>
<span class="sudo-text">Sudoing as <strong>{state.me.name}</strong> ({state.me.email})</span>
<ui-button size="sm" icon="x" label="Stop sudo" onclick="fez.stopSudo()"></ui-button>
</div>
{/if}
<nav class="rail" aria-label="Main" data-tip-side="right" data-tip-gap="18">
<a class="rail-link" class:active={state.inMail} href="/#/inbox" title="Mail" aria-label="Mail"><ui-icon name="mailbox" size="22"></ui-icon></a>
<a class="rail-link" class:active={state.inCalendar} href="/#/calendar" title="Calendar" aria-label="Calendar"><ui-icon name="calendar-blank" size="22"></ui-icon></a>
{#if state.person}
<a class="rail-link" class:active={state.view === 'contacts'} href="/#/contacts" title="Contacts" aria-label="Contacts"><ui-icon name="address-book" size="22"></ui-icon></a>
{/if}
{#if state.operator}
<a class="rail-link" class:active={state.inAdmin} href="/admin" title="Admin" aria-label="Admin"><ui-icon name="buildings" size="22"></ui-icon></a>
{/if}
<span class="spacer"></span>
<button class="rail-link" type="button" title={state.isDark ? 'Light theme' : 'Dark theme'} aria-label={state.isDark ? 'Light theme' : 'Dark theme'} onclick="fez.toggleTheme()">{#if state.isDark}<ui-icon name="sun" size="22"></ui-icon>{:else}<ui-icon name="moon" size="22"></ui-icon>{/if}</button>
<a class="rail-link" class:active={state.view === 'help'} href="/#/help" title="Help" aria-label="Help"><ui-icon name="question" size="22"></ui-icon></a>
<button class="me" class:active={state.inAccount} type="button" title="Account" aria-label="Account" popovertarget="me-menu"><ui-avatar :user="state.me" size="md"></ui-avatar></button>
</nav>
<div id="me-menu" class="me-menu ui-card elevated" popover ontoggle="fez.menuToggled(event)">
{#if state.renaming}
<form class="me-rename" onsubmit="fez.saveName(event)" novalidate>
<ui-input name="name" label="Your name" value={state.me.name} maxlength="80" error={state.profileErrors.name} autofocus></ui-input>
<ui-input name="username" label="Username" value={state.me.username || ''} maxlength="32" hint={state.systemAdmin ? '3 to 32 of a-z, 0-9, ., - and _.' : '6 to 32 of a-z, 0-9, ., - and _, with at least one _ or . (e.g. ana_horvat).'} error={state.profileErrors.username}></ui-input>
<div class="me-actions">
<ui-button type="button" size="sm" variant="ghost" label="Cancel" onclick="fez.cancelRename()"></ui-button>
<ui-button type="submit" size="sm" variant="primary" label="Save" :loading="state.savingName"></ui-button>
</div>
</form>
{:else}
<div class="me-head">
<ui-avatar :user="state.me" size="md"></ui-avatar>
<div class="me-who">
<strong class="me-name">{state.me.name}</strong>
<span class="me-email">{state.me.email}</span>
{#if state.me.username}<span class="me-email">@{state.me.username}</span>{/if}
</div>
{#if state.person}
<ui-button type="button" size="sm" variant="ghost" icon="pencil-simple" title="Edit profile" aria-label="Edit profile" onclick="fez.startRename()"></ui-button>
{/if}
</div>
{/if}
{#if state.person}
<a class="me-item me-link" href="/#/settings" onclick="fez.closeMe()"><ui-icon name="gear" size="18"></ui-icon><span class="me-label">Settings</span></a>
{#if !state.sudo}
<a class="me-item me-link" href="/#/sessions" onclick="fez.closeMe()"><ui-icon name="devices" size="18"></ui-icon><span class="me-label">Sessions</span></a>
{/if}
<a class="me-item me-link" href="/#/feat" onclick="fez.closeMe()"><ui-icon name="list-checks" size="18"></ui-icon><span class="me-label">Features</span></a>
{/if}
<button class="me-item" type="button" onclick="fez.signOut()"><ui-icon name="sign-out" size="18"></ui-icon><span class="me-label">Sign out</span></button>
</div>
<main>
{#unless state.pageLoading}
<fez-component name={state.page} :props="state.query">
<div class="not-found"><ui-empty icon="warning-circle" title="Page not found" text="This link does not open any page."></ui-empty></div>
</fez-component>
{/unless}
</main>
</div>
{/if}`
};
}

Fez('app-shell', __fez_component_0);

export default __fez_component_0;
